import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Pin, Trash2, Loader2, Check, CloudOff, StickyNote, Copy, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { useNotes } from "../lib/notes-context";
import { saveDraft, removeDraft } from "../lib/note-drafts";
import ConfirmDialog from "./ConfirmDialog";

// Paleta de cores das notas (mesmos nomes aceitos pelo backend)
export const NOTE_COLORS = [
    { id: "", dot: "bg-zinc-600", label: "Sem cor" },
    { id: "green", dot: "bg-emerald-500", label: "Verde" },
    { id: "blue", dot: "bg-sky-500", label: "Azul" },
    { id: "purple", dot: "bg-violet-500", label: "Roxo" },
    { id: "amber", dot: "bg-amber-500", label: "Âmbar" },
    { id: "rose", dot: "bg-rose-500", label: "Rosa" },
    { id: "slate", dot: "bg-slate-400", label: "Cinza" },
];

/**
 * Editor de nota — modal global.
 * Mobile: tela cheia (estilo app). Desktop: painel grande centralizado.
 *
 * Autosave "à prova de balas":
 *   1. Cada alteração é gravada IMEDIATAMENTE no localStorage (rascunho local).
 *   2. Em paralelo, salva no servidor com debounce (~900ms).
 *   3. Se o servidor/internet falhar: mostra "Salvo no dispositivo" e fica
 *      re-tentando a cada 5s (+ quando a internet volta, evento `online`).
 *   4. Fechar a página/app não perde nada (flush no beforeunload/visibilitychange);
 *      rascunhos pendentes são sincronizados pelo NotesProvider quando possível.
 */
export default function NoteEditor() {
    const { editor } = useNotes();
    if (!editor.open) return null;
    return <EditorInner key={editor.key} />;
}

function EditorInner() {
    const { editor, closeEditor, createNote, updateNote, removeNote, setOpenDraftId } = useNotes();
    const note = editor.note;

    const [title, setTitle] = useState(note?.title || "");
    const [content, setContent] = useState(note?.content || "");
    const [pinned, setPinned] = useState(!!note?.pinned);
    const [color, setColor] = useState(note?.color || "");
    const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | offline | error
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [copied, setCopied] = useState(false);

    const noteIdRef = useRef(note?.id || null);
    // Id do rascunho local: id da nota, ou um id temporário para nota nova.
    const draftIdRef = useRef(note?.id || `new:${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const dirtyRef = useRef(false);
    const deletedRef = useRef(false);
    const saveTimerRef = useRef(null);
    const draftTimerRef = useRef(null);
    const retryTimerRef = useRef(null);
    const savingRef = useRef(false);
    const latestRef = useRef({ title, content, pinned, color });
    latestRef.current = { title, content, pinned, color };

    // Registra no provider qual rascunho está aberto (o sync em background pula ele)
    useEffect(() => {
        setOpenDraftId(draftIdRef.current);
        return () => setOpenDraftId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---------------- Rascunho local (localStorage) ----------------
    const writeDraftNow = useCallback(() => {
        if (deletedRef.current) return;
        const { title: t, content: c, pinned: p, color: col } = latestRef.current;
        if (!t.trim() && !c.trim()) return;
        saveDraft(draftIdRef.current, { noteId: noteIdRef.current, title: t, content: c, pinned: p, color: col });
    }, []);

    const scheduleDraft = useCallback(() => {
        if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
        draftTimerRef.current = setTimeout(writeDraftNow, 250);
    }, [writeDraftNow]);

    // Flush do rascunho ao sair da página / trocar de app no celular
    useEffect(() => {
        const flush = () => { if (dirtyRef.current) writeDraftNow(); };
        const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
        window.addEventListener("beforeunload", flush);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("beforeunload", flush);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [writeDraftNow]);

    // Quando a nota completa termina de carregar (openNote), hidrata o form.
    useEffect(() => {
        if (note && !dirtyRef.current) {
            noteIdRef.current = note.id;
            // Migra o id do rascunho para o id real da nota
            if (draftIdRef.current !== note.id) {
                removeDraft(draftIdRef.current);
                draftIdRef.current = note.id;
                setOpenDraftId(note.id);
            }
            setTitle(note.title || "");
            setContent(note.content || "");
            setPinned(!!note.pinned);
            setColor(note.color || "");
            // Veio de rascunho local (offline/queda) → precisa ir pro servidor
            if (note._fromDraft) {
                dirtyRef.current = true;
                setTimeout(() => { latestRef.current && persistRef.current?.(); }, 400);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [note?.id]);

    // ---------------- Autosave no servidor ----------------
    const persistRef = useRef(null);
    const persist = useCallback(async () => {
        if (deletedRef.current) return;
        const snap = { ...latestRef.current };
        if (!snap.title.trim() && !snap.content.trim()) return; // nada para salvar ainda
        if (savingRef.current) return;
        savingRef.current = true;
        setSaveState("saving");
        try {
            if (!noteIdRef.current) {
                const created = await createNote({ title: snap.title, content: snap.content, pinned: snap.pinned, color: snap.color });
                noteIdRef.current = created.id;
                // Migra o rascunho temporário para o id definitivo
                removeDraft(draftIdRef.current);
                draftIdRef.current = created.id;
                setOpenDraftId(created.id);
            } else {
                await updateNote(noteIdRef.current, { title: snap.title, content: snap.content, pinned: snap.pinned, color: snap.color });
            }
            const cur = latestRef.current;
            const unchanged = cur.title === snap.title && cur.content === snap.content && cur.pinned === snap.pinned && cur.color === snap.color;
            if (unchanged) {
                dirtyRef.current = false;
                removeDraft(draftIdRef.current); // servidor tem a versão final
                setSaveState("saved");
            } else {
                // usuário continuou digitando durante o save — garante nova rodada
                writeDraftNow();
                if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
                saveTimerRef.current = setTimeout(() => persistRef.current?.(), 900);
                setSaveState("saved");
            }
        } catch (err) {
            // Falhou (sem internet / servidor fora): rascunho local segura tudo.
            writeDraftNow();
            const isOffline = !navigator.onLine || !err?.response;
            setSaveState(isOffline ? "offline" : "error");
            // Re-tenta sozinho em 5s enquanto o editor estiver aberto
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
            retryTimerRef.current = setTimeout(() => persistRef.current?.(), 5000);
        } finally {
            savingRef.current = false;
        }
    }, [createNote, updateNote, setOpenDraftId, writeDraftNow]);
    persistRef.current = persist;

    const scheduleSave = useCallback(() => {
        dirtyRef.current = true;
        scheduleDraft(); // rascunho local primeiro — nunca perde nada
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => persistRef.current?.(), 900);
    }, [scheduleDraft]);

    // Internet voltou → salva na hora
    useEffect(() => {
        const onOnline = () => { if (dirtyRef.current) persistRef.current?.(); };
        window.addEventListener("online", onOnline);
        return () => window.removeEventListener("online", onOnline);
    }, []);

    useEffect(() => () => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    }, []);

    const handleClose = useCallback(async () => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        if (dirtyRef.current) {
            writeDraftNow(); // garante o rascunho antes de qualquer coisa
            await persist();
            if (dirtyRef.current) {
                // ainda não conseguiu enviar — o NotesProvider sincroniza depois
                toast.info("Sem conexão — a nota está salva no dispositivo e será sincronizada automaticamente.");
            }
        }
        closeEditor();
    }, [persist, closeEditor, writeDraftNow]);

    // ESC fecha (salvando antes)
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") handleClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [handleClose]);

    const handleDelete = async () => {
        if (!noteIdRef.current) {
            deletedRef.current = true;
            removeDraft(draftIdRef.current);
            closeEditor();
            return;
        }
        setDeleting(true);
        try {
            deletedRef.current = true;
            await removeNote(noteIdRef.current);
            removeDraft(draftIdRef.current);
            toast.success("Nota excluída.");
        } catch {
            deletedRef.current = false;
            toast.error("Não foi possível excluir a nota.");
            setDeleting(false);
            setConfirmDelete(false);
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(content || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {
            toast.error("Não foi possível copiar.");
        }
    };

    const chars = content.length;
    const words = content.trim() ? content.trim().split(/\s+/).length : 0;

    return (
        <div className="fixed inset-0 z-[80] flex md:items-center md:justify-center fade-in" data-testid="note-editor">
            {/* Clicar fora NÃO fecha o editor — evita fechar sem querer */}
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
            <div className="relative w-full h-[100dvh] md:h-[85vh] md:max-w-3xl md:rounded-xl bg-[#0d0d0d] border-border md:border md:shadow-2xl flex flex-col overflow-hidden">
                {/* Header */}
                <div className="shrink-0 flex items-center gap-1.5 md:gap-2 px-3 md:px-4 h-14 border-b border-border">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="p-2 -ml-1 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        aria-label="Fechar"
                        data-testid="note-editor-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <SaveStatus state={saveState} />
                    <div className="flex-1" />
                    <button
                        type="button"
                        onClick={handleCopy}
                        className={`p-2 rounded-sm transition-colors ${copied ? "text-emerald-400 bg-emerald-500/10" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                        aria-label="Copiar conteúdo da nota"
                        title={copied ? "Copiado!" : "Copiar conteúdo"}
                        data-testid="note-editor-copy"
                    >
                        {copied ? <Check className="w-4.5 h-4.5" /> : <Copy className="w-4.5 h-4.5" />}
                    </button>
                    <button
                        type="button"
                        onClick={() => { setPinned((v) => !v); scheduleSave(); }}
                        className={`p-2 rounded-sm transition-colors ${pinned ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                        aria-label={pinned ? "Desafixar nota" : "Fixar nota"}
                        title={pinned ? "Desafixar" : "Fixar"}
                        data-testid="note-editor-pin"
                    >
                        {pinned ? <Pin className="w-4.5 h-4.5 fill-current" /> : <Pin className="w-4.5 h-4.5" />}
                    </button>
                    <button
                        type="button"
                        onClick={() => setConfirmDelete(true)}
                        className="p-2 rounded-sm text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        aria-label="Excluir nota"
                        title="Excluir"
                        data-testid="note-editor-delete"
                    >
                        <Trash2 className="w-4.5 h-4.5" />
                    </button>
                </div>

                {editor.loading ? (
                    <div className="flex-1 flex items-center justify-center text-muted-foreground">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Corpo */}
                        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => { setTitle(e.target.value); scheduleSave(); }}
                                placeholder="Título da nota"
                                maxLength={200}
                                autoFocus={!note}
                                data-testid="note-editor-title"
                                className="shrink-0 w-full bg-transparent px-4 md:px-6 pt-4 pb-2 text-xl md:text-2xl font-display font-bold tracking-tight placeholder:text-muted-foreground/50 focus:outline-none"
                            />
                            <textarea
                                value={content}
                                onChange={(e) => { setContent(e.target.value); scheduleSave(); }}
                                placeholder="Escreva sua nota aqui… (sem limite de tamanho)"
                                data-testid="note-editor-content"
                                className="flex-1 w-full min-h-[50vh] md:min-h-0 bg-transparent px-4 md:px-6 py-2 text-[15px] leading-relaxed text-foreground/90 placeholder:text-muted-foreground/40 focus:outline-none resize-none"
                            />
                        </div>

                        {/* Footer */}
                        <div className="shrink-0 border-t border-border px-3 md:px-6 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 safe-bottom">
                            <div className="flex items-center" data-testid="note-editor-colors">
                                {NOTE_COLORS.map((c) => (
                                    <button
                                        key={c.id || "none"}
                                        type="button"
                                        onClick={() => { setColor(c.id); scheduleSave(); }}
                                        title={c.label}
                                        aria-label={c.label}
                                        aria-pressed={color === c.id}
                                        className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center border-2 transition-colors ${
                                            color === c.id ? "border-white/80 bg-white/10" : "border-transparent hover:bg-white/5"
                                        }`}
                                    >
                                        <span className={`block w-4 h-4 rounded-full ${c.dot} ${color === c.id ? "" : "opacity-70"}`} />
                                    </button>
                                ))}
                            </div>
                            <div className="ml-auto text-[10px] font-mono text-muted-foreground whitespace-nowrap">
                                {words} palavras · {chars.toLocaleString("pt-BR")} caracteres
                            </div>
                        </div>
                    </>
                )}
            </div>

            <ConfirmDialog
                open={confirmDelete}
                title="Excluir nota?"
                description="Essa ação não pode ser desfeita."
                confirmLabel="Excluir"
                destructive
                loading={deleting}
                onConfirm={handleDelete}
                onCancel={() => setConfirmDelete(false)}
                testId="note-delete-confirm"
            />
        </div>
    );
}

function SaveStatus({ state }) {
    if (state === "saving") {
        return (
            <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-testid="note-save-status">
                <Loader2 className="w-3 h-3 animate-spin" /> Salvando…
            </span>
        );
    }
    if (state === "saved") {
        return (
            <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-emerald-400" data-testid="note-save-status">
                <Check className="w-3 h-3" /> Salvo
            </span>
        );
    }
    if (state === "offline") {
        return (
            <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-amber-400" data-testid="note-save-status" title="Sem conexão — a nota está protegida no dispositivo e será sincronizada automaticamente.">
                <HardDrive className="w-3 h-3" /> Salvo no dispositivo
            </span>
        );
    }
    if (state === "error") {
        return (
            <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-amber-400" data-testid="note-save-status" title="Falha no servidor — a nota está protegida no dispositivo; tentando de novo…">
                <CloudOff className="w-3 h-3" /> Protegido no dispositivo
            </span>
        );
    }
    return (
        <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground/60" data-testid="note-save-status">
            <StickyNote className="w-3 h-3" /> Nota
        </span>
    );
}
